import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Building2, Globe2, Landmark, Phone, Upload } from "lucide-react";
import { Button } from "@veorest/ui";
import {
  fetchAdminIdentity,
  updateAdminIdentity,
  uploadPlatformAsset,
  type AdminIdentity,
} from "@veorest/api-client";
import { SettingsCard } from "./SettingsCard";

const TIMEZONES = [
  "Asia/Baghdad",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Europe/Istanbul",
  "Europe/London",
  "UTC",
];

/**
 * OELOX Identity (Settings → Identity) — the canonical platform record:
 * brand, support, legal/invoice identity and platform defaults
 * (currency IQD default / USD, language Arabic default / ku / en, Iraq timezone).
 */
export function IdentitySettingsPanel() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["admin-identity"], queryFn: fetchAdminIdentity });
  const [form, setForm] = useState<AdminIdentity | null>(null);

  useEffect(() => {
    if (query.data && !form) setForm(query.data);
  }, [query.data, form]);

  const set = (patch: Partial<AdminIdentity>) =>
    setForm((f) => (f ? { ...f, ...patch } : f));

  const upload = useMutation({
    mutationFn: (file: File) => uploadPlatformAsset(file),
    onSuccess: (url) => {
      set({ logo_url: url });
      toast.success("Logo uploaded — save to apply");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("Identity not loaded");
      if (!form.brand_name_en.trim()) throw new Error("English brand name is required");
      return updateAdminIdentity({ ...form, brand_name_en: form.brand_name_en.trim() });
    },
    onSuccess: (data) => {
      setForm(data);
      toast.success("Identity saved");
      void qc.invalidateQueries({ queryKey: ["admin-identity"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (query.isLoading || !form) {
    return <p className="text-sm font-semibold text-[var(--text-muted)]">Loading identity…</p>;
  }
  if (query.isError) {
    return (
      <p className="text-sm font-semibold text-red-500">
        Could not load identity: {(query.error as Error)?.message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <SettingsCard
        icon={Building2}
        title="Brand"
        subtitle="The platform name and logo — shown in this console today, on invoices and emails next."
        actions={
          <Button className="min-h-[40px]" loading={save.isPending} onClick={() => save.mutate()}>
            Save identity
          </Button>
        }
        footnote="One save applies every section below — identity is stored as a single validated record."
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)]">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Platform logo" className="h-full w-full object-contain" />
            ) : (
              <Building2 className="h-6 w-6 text-[var(--text-muted)]" />
            )}
          </div>
          <label className="btn-secondary relative inline-flex cursor-pointer items-center gap-2 overflow-hidden px-4 py-2.5">
            <Upload className="h-4 w-4" />
            {upload.isPending ? "Uploading…" : "Upload logo"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="absolute inset-0 cursor-pointer opacity-0"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = "";
              }}
            />
          </label>
          {form.logo_url ? (
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => set({ logo_url: null })}
            >
              Remove
            </button>
          ) : null}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <label className="block space-y-1">
            <span className="text-sm font-bold">Brand name (English) *</span>
            <input
              className="input-base"
              value={form.brand_name_en}
              onChange={(e) => set({ brand_name_en: e.target.value })}
              placeholder="OELOX"
            />
          </label>
          <label className="block space-y-1" dir="rtl">
            <span className="text-sm font-bold" dir="ltr">
              الاسم بالعربية
            </span>
            <input
              className="input-base"
              value={form.brand_name_ar}
              onChange={(e) => set({ brand_name_ar: e.target.value })}
              placeholder="أويلوكس"
            />
          </label>
          <label className="block space-y-1" dir="rtl">
            <span className="text-sm font-bold" dir="ltr">
              ناوی کوردی
            </span>
            <input
              className="input-base"
              value={form.brand_name_ku}
              onChange={(e) => set({ brand_name_ku: e.target.value })}
              placeholder="ئۆیلۆکس"
            />
          </label>
        </div>
      </SettingsCard>

      <SettingsCard
        icon={Phone}
        title="Support & contact"
        subtitle="Public support channels used across client, partner and driver surfaces."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <label className="block space-y-1">
            <span className="text-sm font-bold">Support email</span>
            <input
              className="input-base"
              type="email"
              value={form.support_email ?? ""}
              onChange={(e) => set({ support_email: e.target.value || null })}
              placeholder="support@oelox.com"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-bold">Support phone</span>
            <input
              className="input-base"
              value={form.support_phone ?? ""}
              onChange={(e) => set({ support_phone: e.target.value || null })}
              placeholder="+964 …"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-bold">Website</span>
            <input
              className="input-base"
              value={form.website ?? ""}
              onChange={(e) => set({ website: e.target.value || null })}
              placeholder="https://oelox.com"
            />
          </label>
        </div>
      </SettingsCard>

      <SettingsCard
        icon={Landmark}
        title="Legal & invoice identity"
        subtitle="Used on invoices, statements and official documents."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-sm font-bold">Legal name</span>
            <input
              className="input-base"
              value={form.legal_name}
              onChange={(e) => set({ legal_name: e.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-bold">Tax / registration number</span>
            <input
              className="input-base"
              value={form.tax_number}
              onChange={(e) => set({ tax_number: e.target.value })}
            />
          </label>
          <label className="block space-y-1 md:col-span-2">
            <span className="text-sm font-bold">Address</span>
            <input
              className="input-base"
              value={form.address}
              onChange={(e) => set({ address: e.target.value })}
              placeholder="Baghdad, Iraq"
            />
          </label>
          <label className="block space-y-1 md:col-span-2">
            <span className="text-sm font-bold">Invoice footer</span>
            <textarea
              className="input-base min-h-[72px]"
              value={form.invoice_footer}
              onChange={(e) => set({ invoice_footer: e.target.value })}
              placeholder="Thank you for doing business with OELOX."
            />
          </label>
        </div>
      </SettingsCard>

      <SettingsCard
        icon={Globe2}
        title="Platform defaults"
        subtitle="Applied to new clients, partners and drivers — and the default language policy for all apps."
        footnote="Arabic is the platform default. Supported languages ship in ar / ku / en; supported currencies are IQD and USD (Dinar is the default)."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <label className="block space-y-1">
            <span className="text-sm font-bold">Default currency</span>
            <select
              className="input-base"
              value={form.default_currency}
              onChange={(e) => set({ default_currency: e.target.value === "USD" ? "USD" : "IQD" })}
            >
              <option value="IQD">IQD — Iraqi Dinar (default)</option>
              <option value="USD">USD — US Dollar</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-bold">Default language</span>
            <select
              className="input-base"
              value={form.default_language}
              onChange={(e) =>
                set({
                  default_language:
                    e.target.value === "ku" ? "ku" : e.target.value === "en" ? "en" : "ar",
                })
              }
            >
              <option value="ar">Arabic (default)</option>
              <option value="ku">Kurdish</option>
              <option value="en">English</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-bold">Timezone</span>
            <select
              className="input-base"
              value={form.timezone}
              onChange={(e) => set({ timezone: e.target.value })}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                  {tz === "Asia/Baghdad" ? " — Iraq (default)" : ""}
                </option>
              ))}
              {!TIMEZONES.includes(form.timezone) ? (
                <option value={form.timezone}>{form.timezone}</option>
              ) : null}
            </select>
          </label>
        </div>
      </SettingsCard>
    </div>
  );
}
